import {
	AfterViewInit,
	Component,
	DestroyRef, ElementRef,
	EventEmitter,
	forwardRef,
	inject,
	Input, OnChanges,
	Output, QueryList, SimpleChanges,
	ViewChildren,
} from '@angular/core';
import { ScrollingModule } from '@angular/cdk/scrolling';
import { A11yModule } from '@angular/cdk/a11y';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';
import { NgClass, NgStyle } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { debounceTime, Subject } from 'rxjs';
import { TranslocoPipe } from '@jsverse/transloco';
import { BadgeComponent } from '../badge/badge.component';
import { NgSelectModule } from '@ng-select/ng-select';
import { S3Service } from '../../../app/services/s3.service';
import { SearchHelperService } from '../../../app/services/search-helper.service';

@Component({
	selector: 'mos-split-list-dropdown',
	standalone: true,
	imports: [FormsModule, ScrollingModule, TranslocoPipe, A11yModule, BadgeComponent, NgSelectModule, NgStyle, NgClass],
	templateUrl: './split-list-dropdown.component.html',
	providers: [
		{
			provide: NG_VALUE_ACCESSOR,
			useExisting: forwardRef(() => SplitListDropdownComponent),
			multi: true,
		},
	],
})
export class SplitListDropdownComponent implements ControlValueAccessor, AfterViewInit, OnChanges {
	@Input() public appendTo: any;
	@Input() public appendToBody: boolean = false;
	@Input() public labelKey: 'name' | 'title' | 'labourType.title' | 'combinedLabel' | '';
	@Input() public bindValue: 'id' | '_id' | '';
	@Input() public dataType: 'Brands' | 'Coating Systems' | 'Colours' | 'Products' | 'Labour Types' | 'Tools' | 'Divisions' | '' = '';
	@Input() public placeholder: string;
	@Input() public disabled: boolean = false;
	@Input() public loading: boolean = false;
	@Input() public clearable: boolean = true;
	@Input() public items: any[] = [];
	@Input() public groupBy: string = 'documentType';
	@Input() public useSearch: boolean = true;
	@Input() public showDocumentType: boolean = false;

	@Output() public itemSelected: EventEmitter<any> = new EventEmitter();
	@Output() public clear: EventEmitter<void> = new EventEmitter();
	@Output() public scrollToEnd: EventEmitter<void> = new EventEmitter();
	@Output() public search: EventEmitter<string> = new EventEmitter();

	@ViewChildren('lazyImg', { read: ElementRef }) public lazyImages!: QueryList<ElementRef>;

	public filteredItems: any[] = [];
	public lastSearchTerm: string = '';
	public searchSubject = new Subject<string>();
	public selectedItem: any;
	private _items: any[] = [];
	private _ngModelValue: any;
	private destroyRef = inject(DestroyRef);
	private imageObserver?: IntersectionObserver;
	private onChangeFn: (val: number) => void = () => {};
	private onTouchedFn: () => void = () => {};

	constructor(
		private s3Service: S3Service,
		private searchHelperService: SearchHelperService,) {
	}

	public ngAfterViewInit(): void {
		this.searchSubject.pipe(debounceTime(300),
			takeUntilDestroyed(this.destroyRef))
			.subscribe((term: string) => {
				this.lastSearchTerm = term;
				if (this.useSearch) this.filterItems(term);
				// this is used when the parent component has their own search functionality
				this.search.emit(term);
			});

		this.observeVisibleImages();
		this.lazyImages.changes.subscribe(() => {
			this.observeVisibleImages();
		});
	}

	public ngOnChanges(changes: SimpleChanges): void {
		if (changes['labelKey'] || changes['items']) {
			this.processItems();
		}
	}

	private processItems(): void {
		if (!this.items?.length) {
			this._items = [];
			this.filteredItems = [];
			return;
		}

		if (!this.labelKey) {
			this._items = this.items;
			this.filteredItems = this.items;
			return;
		}

		this._items = this.items.map(item => ({
			...item,
			searchTarget: [
				item?.[this.labelKey] ?? '',
				...(item.tags ?? []),
				this.dataType === 'Colours' ? item?.code : ''
			].join(' ').toLowerCase().trim()
		}));

		// In case of search before items have loaded
		if (this.lastSearchTerm) {
			this.filterItems(this.lastSearchTerm);
		} else {
			this.filteredItems = this._items;
		}

		// Re-select item in case it was missed earlier
		if (this.selectedItem && typeof this.selectedItem !== 'object') {
			this.selectedItem = this.findItemByValue(this.selectedItem);
		} else if (this._ngModelValue && !this.selectedItem) {
			this.selectedItem = this.findItemByValue(this._ngModelValue);
		}
	}

	private filterItems(term: string): void {
		if (!term) {
			this.filteredItems = this._items;
			return;
		}

		if (!this._items.length) {
			this.filteredItems = [];
			return;
		}

		const normalisedTerm = term.toLowerCase();
		this.filteredItems = this._items.filter(item =>
			this.searchHelperService.fuzzyMatch(normalisedTerm, item.searchTarget)
		);
	}

	private observeVisibleImages(): void {
		if (this.imageObserver) {
			this.imageObserver.disconnect();
		}

		this.imageObserver = new IntersectionObserver(entries => {
			for (const entry of entries) {
				if (entry.isIntersecting) {
					const imgEl = entry.target as HTMLImageElement;
					const key = imgEl.dataset['imageKey'];
					const item = this.filteredItems?.find(i =>(i.pictureKey ?? i.logo ?? i.fileKey)  === key);
					if (key && item) {
						this.s3Service.getSignedUrl(key)
							.pipe(takeUntilDestroyed(this.destroyRef))
							.subscribe(url => {
								item.imageUrl = url;
								imgEl.src = url;
							});
					}
					this.imageObserver?.unobserve(imgEl);
				}
			}
		}, { rootMargin: '300px' });

		this.lazyImages.forEach(ref => this.imageObserver?.observe(ref.nativeElement));
	}

	public selectItem(): void {
		const selected = this.bindValue && this.selectedItem
			? this.selectedItem[this.bindValue]
			: this.selectedItem ?? null;
		this.onChangeFn(selected);
		this.onTouchedFn();
		this.itemSelected.emit(selected);
	}

	public onClear(): void {
		this.clear.emit();
	}

	public onScrollToEnd(): void {
		this.scrollToEnd.emit();
	}

	/**
	 * Called by Angular when a new value is written to the form control programmatically
	 * @param value
	 */
	public writeValue(value: any): void {
		this._ngModelValue = value;
		if (!value) {
			this.selectedItem = null;
		} else if (typeof value === 'object') {
			// Full object passed in
			this.selectedItem = value;
		} else {
			// id passed in — find corresponding object
			this.selectedItem = this.findItemByValue(value);
		}
	}

	public getLabel(item: any): string {
		if (!item || !this.labelKey) return '';
		// Handle nested keys like "labourType.title"
		return this.labelKey.split('.').reduce((acc, key) => acc?.[key], item) ?? '';
	}

	public compareId(item: any, selected: any): boolean {
		if (item.id && selected.id) {
			return item.id === selected.id;
		} else {
			return item === selected;
		}
	}

	/**
	 * Finds the
	 * @param value
	 */
	public findItemByValue(value: any): any {
		return this._items.find(item => item[this.bindValue] === value);
	}

	/**
	 * Registers an outside (parent form) onChange function so we can call it whenever our input is updated.
	 * @param fn
	 */
	public registerOnChange(fn: any): void {
		this.onChangeFn = fn;
	}

	/**
	 * Registers an outside (parent form) onTouched function that gets called when our form control is touched.
	 * @param fn
	 */
	public registerOnTouched(fn: any): void {
		this.onTouchedFn = fn;
	}

}
